import { type NavigateFunction } from "react-router-dom";
import {
  NotificationActionType,
  type NotificationDto,
} from "@/features/notifications/model/notifications.types";
import { buildCanonicalBranchAdminUrl } from "@/shared/lib/branch/branch-admin-routing";

type NotificationAction = NonNullable<NotificationDto["action"]>;

/** A branch-admin destination built from the notification's OWN workspace + branch slugs (never the current
 *  context, never a `/dashboard` fallback). Returns null when either slug is missing, so we suppress a dead nav
 *  instead of routing somewhere wrong. */
function branchAdmin(action: NotificationAction, path: string): string | null {
  if (!action.workspaceSlug || !action.branchSlug) return null;
  return buildCanonicalBranchAdminUrl(action.workspaceSlug, action.branchSlug, path);
}

/**
 * Maps every {@link NotificationActionType} to a real tenant route. `satisfies Record<…>` makes this exhaustive —
 * adding an action type is a compile error until it's mapped here, so notifications can never silently fall through
 * to a dead link. Routes are validated against the tenant route registry.
 */
const ACTION_PATH_BUILDERS = {
  [NotificationActionType.None]: () => null,
  // Welcome notifications carry the user's landing branch -> open its dashboard (/w/:ws/b/:branch/). A bare
  // /w/:ws has no route, so fall back to the workspace hub when no branch is present (e.g. an older notification).
  [NotificationActionType.OpenWorkspace]: (action) => branchAdmin(action, "/") ?? "/workspaces",
  // Profile lives in the Clerk "Manage account" menu (no /profile route) — land on the personal workspace hub.
  [NotificationActionType.OpenProfile]: () => "/workspaces",
  [NotificationActionType.OpenSettings]: (action) => branchAdmin(action, "/settings/system/invitations"),
  [NotificationActionType.OpenPledge]: (action) =>
    branchAdmin(action, action.entityId ? `/finance/pledges/${action.entityId}` : "/finance/pledges"),
  [NotificationActionType.OpenBudget]: (action) =>
    branchAdmin(action, action.entityId ? `/budgets/${action.entityId}` : "/budgets"),
  // No transaction detail route — open the list and let it focus the row via the query param.
  [NotificationActionType.OpenFinanceTransaction]: (action) =>
    branchAdmin(
      action,
      action.entityId ? `/finance/transactions?focus=${action.entityId}` : "/finance/transactions",
    ),
  // The transfers tab, not a detail route — a transfer is ACTED on from the list, and the receiving side needs
  // to land where the decision lives.
  [NotificationActionType.OpenFinanceTransfer]: (action) => branchAdmin(action, "/finance/transfers"),
  [NotificationActionType.OpenDepartment]: (action) =>
    branchAdmin(action, action.entityId ? `/departments/${action.entityId}` : "/departments"),
  [NotificationActionType.OpenActivity]: (action) =>
    branchAdmin(action, action.entityId ? `/activities/${action.entityId}` : "/activities"),
  // Only follow a generic legacy URL when it's a safe absolute URL; otherwise suppress (slug-less paths are dead).
  [NotificationActionType.OpenGenericEntity]: (action) =>
    action.legacyUrl && /^https?:\/\//i.test(action.legacyUrl) ? action.legacyUrl : null,
} satisfies Record<NotificationActionType, (action: NotificationAction) => string | null>;

export function resolveNotificationPath(notification: NotificationDto): string | null {
  const action = notification.action;
  if (!action) return null;
  return ACTION_PATH_BUILDERS[action.type]?.(action) ?? null;
}

export function openNotificationAction(notification: NotificationDto, navigate: NavigateFunction) {
  const path = resolveNotificationPath(notification);
  if (!path) return;

  if (/^https?:\/\//i.test(path)) {
    window.location.assign(path);
    return;
  }

  navigate(path);
}
